import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiResponse } from '../types/bewerber';
import type { SaveAnswersDto, Vorstellungsgespraech } from '../types/vorstellungsgespraech';
import { activityKeys } from './activities';
import { bewerberKeys } from './bewerber';
import { apiClient } from './client';

// Query keys
export const vorstellungsgespraechKeys = {
  all: ['vorstellungsgespraech'] as const,
  lists: () => [...vorstellungsgespraechKeys.all, 'list'] as const,
  list: (bewerberId: string) => [...vorstellungsgespraechKeys.lists(), bewerberId] as const,
  details: () => [...vorstellungsgespraechKeys.all, 'detail'] as const,
  detail: (id: string) => [...vorstellungsgespraechKeys.details(), id] as const,
};

const basePath = (bewerberId: string) => `/bewerber/${bewerberId}/vorstellungsgespraech`;

// API functions
const fetchListe = async (bewerberId: string): Promise<ApiResponse<Vorstellungsgespraech[]>> => {
  const response = await apiClient.get(basePath(bewerberId));
  return response.data;
};

const fetchOne = async (
  bewerberId: string,
  id: string,
): Promise<ApiResponse<Vorstellungsgespraech>> => {
  const response = await apiClient.get(`${basePath(bewerberId)}/${id}`);
  return response.data;
};

const createOrGetDraft = async (
  bewerberId: string,
): Promise<ApiResponse<Vorstellungsgespraech>> => {
  const response = await apiClient.post(`${basePath(bewerberId)}/draft`);
  return response.data;
};

const saveAnswers = async ({
  bewerberId,
  id,
  data,
}: {
  bewerberId: string;
  id: string;
  data: SaveAnswersDto;
}): Promise<ApiResponse<Vorstellungsgespraech>> => {
  const response = await apiClient.patch(`${basePath(bewerberId)}/${id}`, data);
  return response.data;
};

const completeVorstellungsgespraech = async ({
  bewerberId,
  id,
}: {
  bewerberId: string;
  id: string;
}): Promise<ApiResponse<Vorstellungsgespraech>> => {
  const response = await apiClient.post(`${basePath(bewerberId)}/${id}/abschliessen`);
  return response.data;
};

// Hooks
export function useVorstellungsgespraechListe(bewerberId: string) {
  return useQuery({
    queryKey: vorstellungsgespraechKeys.list(bewerberId),
    queryFn: () => fetchListe(bewerberId),
    enabled: !!bewerberId,
  });
}

/** Loads one specific interview, e.g. to view a completed one read-only. */
export function useVorstellungsgespraech(bewerberId: string, id: string | null) {
  return useQuery({
    queryKey: vorstellungsgespraechKeys.detail(id ?? ''),
    queryFn: () => fetchOne(bewerberId, id!),
    enabled: !!bewerberId && !!id,
  });
}

export function useCreateOrGetDraft() {
  return useMutation({ mutationFn: createOrGetDraft });
}

/**
 * Autosave. Deliberately invalidates nothing: local state is the source of truth while the
 * user is typing, and a refetch would overwrite what they are in the middle of entering.
 */
export function useSaveAnswers() {
  return useMutation({ mutationFn: saveAnswers });
}

export function useCompleteVorstellungsgespraech() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: completeVorstellungsgespraech,
    onSuccess: (data) => {
      const { bewerberId } = data.data;
      // Completion writes Bewerber fields and adds timeline entries, so refresh both.
      queryClient.invalidateQueries({ queryKey: bewerberKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bewerberKeys.kanban() });
      queryClient.invalidateQueries({ queryKey: bewerberKeys.detail(bewerberId) });
      queryClient.invalidateQueries({ queryKey: bewerberKeys.statistics() });
      queryClient.invalidateQueries({ queryKey: activityKeys.timelines(bewerberId) });
      queryClient.invalidateQueries({ queryKey: vorstellungsgespraechKeys.list(bewerberId) });
    },
  });
}
